import { ReductionCostBand, Scope2Basis } from '../services/reduction-action/reduction-action.model'

// Formatting and arithmetic shared by the reduction plan, the library and the Recommended
// list. Stored amounts are kg CO2e (platform convention); everything here prints tonnes.

const KG_PER_TONNE = 1000

/** Tonnes to one decimal under ten, whole tonnes above, en-GB separators. "0" for nothing. */
export const formatTonnes = (kg: number | null | undefined): string => {
  const t = (Number(kg) || 0) / KG_PER_TONNE
  if (t === 0) return '0'
  if (Math.abs(t) < 0.05) return '<0.1'
  return t.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: Math.abs(t) < 10 ? 1 : 0 })
}

/** "8.1 to 11.4 t", or "9 t" when the ends round to the same figure. */
export const formatTonnesRange = (minKg: number | null | undefined, maxKg: number | null | undefined): string => {
  const lo = formatTonnes(minKg)
  const hi = formatTonnes(maxKg)
  return lo === hi ? `${lo} t` : `${lo} to ${hi} t`
}

/** "5 to 10%" or "8%". */
export const formatPctRange = (minPct: number | string | null | undefined, maxPct: number | string | null | undefined): string => {
  const lo = Math.round(Number(minPct) || 0)
  const hi = Math.round(Number(maxPct) || 0)
  return lo === hi ? `${lo}%` : `${lo} to ${hi}%`
}

/** Whole pounds, en-GB. Library costs and plan costs are GBP only, as the public sources are. */
export const formatGbp = (amount: number | null | undefined): string => {
  if (amount == null || Number.isNaN(Number(amount))) return ''
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(Number(amount))
}

/** "£2,000 to £6,000", "from £500", "up to £3,000", or "" when neither end is known. */
export const formatGbpRange = (minGbp: number | null | undefined, maxGbp: number | null | undefined): string => {
  if (minGbp != null && maxGbp != null) return minGbp === maxGbp ? formatGbp(minGbp) : `${formatGbp(minGbp)} to ${formatGbp(maxGbp)}`
  if (minGbp != null) return `from ${formatGbp(minGbp)}`
  if (maxGbp != null) return `up to ${formatGbp(maxGbp)}`
  return ''
}

/** "about 2 to 4 years", "about 3 years", or "" when unknown. */
export const formatPaybackRange = (min: number | string | null | undefined, max: number | string | null | undefined): string => {
  const lo = min != null ? Number(min) : null
  const hi = max != null ? Number(max) : null
  const year = (n: number) => (n === 1 ? '1 year' : `${n % 1 === 0 ? n : n.toFixed(1)} years`)
  if (lo != null && hi != null) return lo === hi ? `about ${year(lo)}` : `about ${lo % 1 === 0 ? lo : lo.toFixed(1)} to ${year(hi)}`
  if (lo != null) return `from ${year(lo)}`
  if (hi != null) return `within ${year(hi)}`
  return ''
}

export const COST_BAND_LABELS: Record<ReductionCostBand, string> = {
  none: 'No cost',
  low: 'Low cost',
  medium: 'Medium cost',
  high: 'High cost',
}

export const formatCostBand = (band: ReductionCostBand | null | undefined): string => (band ? COST_BAND_LABELS[band] ?? '' : '')

export const SCOPE2_BASIS_LABELS: Record<Scope2Basis, string> = {
  both: 'Both bases',
  market: 'Market-based only',
  location: 'Location-based only',
}

export const SCOPE2_BASIS_HELP: Record<Scope2Basis, string> = {
  both: 'The saving comes off both the location-based and market-based Scope 2 figures (fewer kWh bought).',
  market: 'Only the market-based figure falls, as with a renewable tariff or certificates. The location-based figure uses the grid average and does not change.',
  location: 'Only the location-based figure falls. Rare; use when the market-based figure is already zero.',
}

/**
 * Cost per tonne per year: (capex / lifetime + opex change) / annual tonnes. Lifetime defaults
 * to the row's reduction lifetime, or one year when ongoing. Null when either the cost or the
 * annual reduction is missing, or the reduction is zero. An ordering aid, not an abatement curve.
 */
export const costPerTonnePerYear = ({
  capexGbp,
  annualOpexDeltaGbp,
  annualReductionKg,
  lifetimeYears,
}: {
  capexGbp?: number | null
  annualOpexDeltaGbp?: number | null
  annualReductionKg?: number | string | null
  lifetimeYears?: number | null
}): number | null => {
  const tonnes = (Number(annualReductionKg) || 0) / KG_PER_TONNE
  if (tonnes <= 0) return null
  if (capexGbp == null && annualOpexDeltaGbp == null) return null
  const years = lifetimeYears != null && lifetimeYears > 0 ? lifetimeYears : 1
  const annualCost = (Number(capexGbp) || 0) / years + (Number(annualOpexDeltaGbp) || 0)
  return annualCost / tonnes
}

/** "£120 per tCO2e a year", "saves £40 per tCO2e a year", or "" when not computable. */
export const formatCostPerTonne = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return ''
  const rounded = Math.abs(value) < 10 ? Math.round(value * 10) / 10 : Math.round(value)
  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: Math.abs(value) < 10 ? 1 : 0 }).format(Math.abs(rounded))
  return value < 0 ? `saves ${money} per tCO2e a year` : `${money} per tCO2e a year`
}
